'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import PageHero from '@/components/ui/PageHero';

const values = [
  { icon: 'RocketLaunchIcon', title: 'Entrepreneurship First', desc: 'We build tech that creates business owners, not dependencies.' },
  { icon: 'CpuChipIcon', title: 'Technology-Led', desc: 'Every decision is driven by data, AI, and engineering excellence.' },
  { icon: 'ShieldCheckIcon', title: 'Reliability', desc: '99.8% uptime SLA. When your business runs, our tech runs.' },
  { icon: 'UsersIcon', title: 'Client Partnership', desc: 'We grow when our clients grow. Your success is our metric.' },
];

const teamHighlights = [
  { icon: 'CpuChipIcon', area: 'Enterprise Technology' },
  { icon: 'CodeBracketIcon', area: 'Product Engineering' },
  { icon: 'BuildingLibraryIcon', area: 'Banking & Finance' },
  { icon: 'ChartBarIcon', area: 'Operations & Scale' },
  { icon: 'UserGroupIcon', area: 'Customer Success' },
  { icon: 'BriefcaseIcon', area: 'Strategic Consulting' },
];

const platformCapabilities = [
  { icon: 'DevicePhoneMobileIcon', title: 'White-Label Mobile Ecosystem', desc: 'Fully branded Android and iOS apps for customers, drivers, delivery partners, and fleet owners.' },
  { icon: 'CpuChipIcon', title: 'Unified Operations Dashboard', desc: 'Manage bookings, drivers, vehicles, dispatch, payments, and performance from one command center.' },
  { icon: 'ChatBubbleLeftRightIcon', title: 'Omnichannel Booking', desc: 'Accept bookings through mobile apps, websites, WhatsApp, call centers, and kiosk-based channels.' },
  { icon: 'BoltIcon', title: 'AI-Powered Intelligence', desc: 'Intelligent driver allocation, dynamic routing, demand forecasting, and predictive operational insights.' },
  { icon: 'ChartBarIcon', title: 'Real-Time Analytics', desc: 'Monitor trips, revenue, utilization, customer behavior, and growth metrics with actionable dashboards.' },
  { icon: 'MapPinIcon', title: 'Multi-City Scalability', desc: 'Scale across cities, regions, and states with centralized control and franchise management.' },
];

export default function AboutContent() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">
      {/* Hero */}
      <PageHero className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
                About Us
              </span>
              <h1 className="font-display text-5xl md:text-7xl font-semibold text-white tracking-tight leading-tight mb-6 mt-2">
                Built for Local Businesses.{' '}
                <span className="italic font-light text-white/80">Designed for Massive Scale.</span>
              </h1>
              <p className="text-white/70 text-xl leading-relaxed max-w-3xl animate-on-scroll stagger-2">
                Movio was created with one mission—to empower regional entrepreneurs and local operators with enterprise-grade technology that was once accessible only to large corporations.
              </p>
            </div>
          </div>
        </div>
      </PageHero>

      {/* Motto Banner */}
      <div
        className="py-16 text-center"
        style={{ background: 'linear-gradient(135deg, #0066FF, #075DDF)' }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="animate-on-scroll stagger-1">
            <p className="font-display text-3xl md:text-5xl font-light italic text-white/90 leading-tight">
              "Your Brand. Your Business. Our Technology —
              <br />
              <span className="font-semibold not-italic text-white">Creating Entrepreneurs."</span>
            </p>
          </div>
        </div>
      </div>

      {/* About Us Content */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="animate-on-scroll stagger-1">
              <span className="section-label block mb-3">Our Story</span>
              <h2 className="font-display text-4xl font-semibold text-[#0F172A] mb-6 leading-tight">
                We provide a complete digital operating platform for mobility, logistics, delivery, and hyperlocal services.
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">
                We enable operators to compete, scale, and succeed in an increasingly digital economy. Today, our platform powers operators across multiple cities, creating opportunities for local businesses while transforming the future of mobility in India.
              </p>
            </div>
            <div className="animate-on-scroll stagger-2">
              <div className="bg-[#F5F5F5] rounded-2xl p-8 border border-[rgba(15,23,42,0.07)]">
                <h3 className="font-display text-2xl font-semibold text-[#0F172A] mb-6">Impact in Numbers</h3>
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: '22+', label: 'Cities Operational' },
                    { value: '6', label: 'Partner Companies' },
                    { value: '5,500+', label: 'Daily Trips' },
                    { value: '3M+', label: 'Successful Trips' },
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <div className="font-display text-3xl font-semibold text-gradient mb-1">{s.value}</div>
                      <div className="text-sm text-[#64748B] font-medium">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why We Exist */}
<div className="py-24 bg-[#F5F5F5]">        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-on-scroll stagger-1">
              <span className="section-label block mb-3">Why We Exist</span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-8">
                Empowering Local Businesses to Lead the{' '}
                <span className="italic font-light text-[#0066FF]">Digital Future.</span>
              </h2>
            </div>
            <div className="animate-on-scroll stagger-2 bg-white rounded-2xl p-10 border border-[rgba(15,23,42,0.07)] shadow-card text-left">
              <p className="text-[#475569] text-lg leading-relaxed mb-6">
                Across India, thousands of local operators have spent years building trusted businesses, strong customer relationships, and deep community connections. They understand their cities, their customers, and their markets better than anyone.
              </p>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">
                As customer expectations evolve and digital adoption accelerates, local businesses need technology that helps them grow faster, operate smarter, and serve customers seamlessly—without losing their identity or independence.
              </p>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">
                At Movio, we believe the future belongs to local entrepreneurs. Our mission is to provide world-class technology, intelligent automation, and scalable digital infrastructure that enables regional operators to modernize operations, strengthen their brand, and expand confidently across cities and new business verticals.
              </p>
              <p className="text-[#0F172A] text-lg font-semibold leading-relaxed">
                Because when local businesses grow, entire communities move forward. Movio—Built to empower local operators. Designed to create the next generation of entrepreneurs.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Platform */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-6">
            <span className="section-label block mb-3">Our Platform</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
              One Technology Platform.{' '}
              <span className="italic font-light text-[#0066FF]">Endless Possibilities.</span>
            </h2>
            <p className="text-[#475569] text-lg max-w-3xl mx-auto leading-relaxed">
              Movio empowers local businesses with a complete digital ecosystem designed to launch, operate, and scale with confidence. Built for real-world operations, our platform enables operators to manage every aspect of their business from one intelligent system.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {platformCapabilities.map((cap, i) => (
              <div key={cap.title} className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} bg-[#FFFFFF] rounded-2xl p-7 border border-[rgba(15,23,42,0.07)] group hover:shadow-card transition-all duration-300`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}>
                  <Icon name={cap.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0066FF]" />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-2">{cap.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
          <div className="animate-on-scroll stagger-6 mt-10 text-center">
            <p className="text-[#475569] text-lg italic">
              Your vision. Your brand. <span className="font-semibold text-[#0F172A] not-italic">Powered by Movio.</span>
            </p>
          </div>
        </div>
      </div>

      {/* Values */}
<div className="py-24 bg-[#F5F5F5]">        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 mb-16">
            <span className="section-label block mb-3">Core Values</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              What drives us{' '}
              <span className="italic font-light text-[#0066FF]">every day.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={v.title} className={`animate-on-scroll stagger-${i + 2} bg-white rounded-2xl p-8 border border-[rgba(15,23,42,0.07)] shadow-card group hover:shadow-card-hover transition-all duration-300`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}>
                  <Icon name={v.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0066FF]" />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-3">{v.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-12">
            <span className="section-label block mb-3">Team</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
              Built by{' '}
              <span className="italic font-light text-[#0066FF]">Industry Veterans.</span>
            </h2>
            <p className="text-[#475569] text-lg max-w-3xl mx-auto leading-relaxed">
              Our leadership team brings decades of global experience across enterprise technology, product engineering, banking, operations, customer success, and strategic consulting. Together, we are building technology that creates entrepreneurs, strengthens local economies, and drives digital inclusion.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {teamHighlights.map((item, i) => (
              <div key={item.area} className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} bg-[#FFFFFF] rounded-2xl p-6 border border-[rgba(15,23,42,0.07)] text-center group hover:shadow-card transition-all duration-300`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}>
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0066FF]" />
                </div>
                <p className="text-sm font-semibold text-[#0F172A] leading-tight">{item.area}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl border border-[#E2E8F0] shadow-2xl p-12 md:p-16 text-center relative overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 45%, #EAF4FF 100%)',
            }}
          >
            <div className="max-w-2xl mx-auto">
              <div className="animate-on-scroll stagger-1">
                <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] mb-4">
                  Ready to Digitize and Scale Your Business?
                </h2>

                <p className="text-[#475569] text-lg mb-8">
                  Book a Demo. Become a Partner. Build Your Future.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    href="/demo-booking"
                    className="btn-primary inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold"
                  >
                    Book a Demo
                    <Icon name="ArrowRightIcon" size={18} />
                  </Link>

                  <Link
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-[#0F172A] border border-[#CBD5E1] hover:bg-white hover:border-[#94A3B8] transition-all"
                  >
                    Partner With Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}